import React, { useEffect, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addSubCollections } from "../../Redux/Actions/collectionsResaleActions";
import AddImage from "../../Global Functions/addImage";
import toast from "react-hot-toast";
import "./addSubCollections.css";

const UploadIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const UploadField = ({ label, fieldKey, value, onUpload, onRemove, loading, required }) => (
  <div className="asc-field">
    <label className="asc-label">
      {label}
      {required ? <span className="asc-req"> *</span> : <span className="asc-optional"> (Optional)</span>}
    </label>
    {value ? (
      <div className="asc-image-preview">
        <img src={value} alt={label} className="asc-preview-img" />
        <div className="asc-preview-info">
          <span className="asc-preview-label">Uploaded</span>
          <button type="button" className="asc-remove-btn" onClick={() => onRemove(fieldKey)} disabled={loading}>
            <TrashIcon />
            Remove
          </button>
        </div>
      </div>
    ) : (
      <label className="asc-upload-zone" htmlFor={`asc-upload-${fieldKey}`}>
        <input
          id={`asc-upload-${fieldKey}`}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => onUpload(e, fieldKey)}
          disabled={loading}
        />
        <div className="asc-upload-icon"><UploadIcon /></div>
        <span className="asc-upload-text">Click to upload</span>
        <span className="asc-upload-hint">PNG, JPG, SVG up to 2MB</span>
      </label>
    )}
  </div>
);

const AddSubCollections = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState(0);

  const [subCollectionData, setSubCollectionData] = useState({
    name: "",
    position: Math.floor(Math.random() * 1000) + 1,
    image: "",
    parent: "",
    icon: "",
    banner_image: "",
    banner_title: "",
    three_d_icon: "",
    collection_id: "",
  });

  useEffect(() => {
    setSubCollectionData((prev) => ({ ...prev, parent: id, collection_id: id }));
  }, [id]);

  const handleFileChange = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    const imageUrl = await AddImage(file);
    if (imageUrl) {
      setSubCollectionData((prev) => ({ ...prev, [type]: imageUrl }));
    }
  };

  const handleRemove = (fieldKey) => {
    setSubCollectionData((prev) => ({ ...prev, [fieldKey]: "" }));
  };

  const handleNextSection = () => {
    if (activeSection === 0 && !subCollectionData.name.trim()) {
      toast.error("Sub-collection name is required");
      return;
    }
    setActiveSection((s) => Math.min(sections.length - 1, s + 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!subCollectionData.name.trim()) return toast.error("Sub-collection name is required");

    setLoading(true);
    try {
      const response = await dispatch(addSubCollections(subCollectionData));
      if (response?.statusCode === 200) {
        toast.success("Sub-collection added successfully!");
        navigate(`/collections_resale/view/${id}`);
      } else {
        toast.error(response?.message || "Failed to add sub-collection");
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  const sections = ["Basic Info", "Media"];

  return (
    <MainLayout>
      <div className="asc-wrapper">
        <div className="asc-header">
          <div className="asc-header-left">
            <div className="asc-header-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                <line x1="12" y1="11" x2="12" y2="17" />
                <line x1="9" y1="14" x2="15" y2="14" />
              </svg>
            </div>
            <div>
              <h1 className="asc-title">Add Sub Collection</h1>
              <p className="asc-subtitle">Fill in the details to add a new sub-collection</p>
            </div>
          </div>
          <Link to={`/collections_resale/view/${id}`} className="asc-view-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
            View Sub-Collection List
          </Link>
        </div>

        <div className="asc-nav">
          {sections.map((sec, i) => (
            <button
              key={i}
              className={`asc-nav-pill ${activeSection === i ? "asc-nav-pill--active" : ""}`}
              onClick={() => setActiveSection(i)}
              type="button"
              disabled={loading}
            >
              <span className="asc-nav-num">{String(i + 1).padStart(2, "0")}</span>
              {sec}
            </button>
          ))}
        </div>

        <div className="asc-form">
          <div className={`asc-section ${activeSection === 0 ? "asc-section--visible" : ""}`}>
            <div className="asc-section-header">
              <span className="asc-section-badge">01</span>
              <h2 className="asc-section-title">Basic Information</h2>
            </div>
            <div className="asc-grid asc-grid-2">
              <div className="asc-field">
                <label className="asc-label">Sub Collection Name <span className="asc-req">*</span></label>
                <input
                  className="asc-input"
                  type="text"
                  value={subCollectionData.name}
                  onChange={(e) => setSubCollectionData((prev) => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter Name"
                  disabled={loading}
                />
              </div>
              <div className="asc-field">
                <label className="asc-label">Banner Title <span className="asc-optional">(Optional)</span></label>
                <input
                  className="asc-input"
                  type="text"
                  value={subCollectionData.banner_title}
                  onChange={(e) => setSubCollectionData((prev) => ({ ...prev, banner_title: e.target.value }))}
                  placeholder="Enter Title"
                  disabled={loading}
                />
              </div>
            </div>
          </div>

          <div className={`asc-section ${activeSection === 1 ? "asc-section--visible" : ""}`}>
            <div className="asc-section-header">
              <span className="asc-section-badge">02</span>
              <h2 className="asc-section-title">Media</h2>
            </div>
            <div className="asc-grid asc-grid-2">
              <UploadField
                label="Sub-Collection Image"
                fieldKey="image"
                value={subCollectionData.image}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="Sub-Collection Icon"
                fieldKey="icon"
                value={subCollectionData.icon}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="Banner Image"
                fieldKey="banner_image"
                value={subCollectionData.banner_image}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
              <UploadField
                label="3D Icon"
                fieldKey="three_d_icon"
                value={subCollectionData.three_d_icon}
                onUpload={handleFileChange}
                onRemove={handleRemove}
                loading={loading}
              />
            </div>
          </div>

          <div className="asc-footer">
            <div className="asc-footer-nav">
              <button
                type="button"
                className="asc-btn asc-btn--ghost"
                onClick={() => setActiveSection((s) => Math.max(0, s - 1))}
                disabled={activeSection === 0 || loading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              {activeSection < sections.length - 1 ? (
                <button type="button" className="asc-btn asc-btn--primary" onClick={handleNextSection} disabled={loading}>
                  Next
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} className="asc-btn asc-btn--submit" disabled={loading}>
                  {loading ? "Saving..." : (
                    <>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Save Sub Collection
                    </>
                  )}
                </button>
              )}
            </div>
            <Link to={`/collections_resale/view/${id}`} className="asc-btn asc-btn--cancel">
              Cancel
            </Link>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AddSubCollections;